import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { Users, FileText, Briefcase, Loader2, AlertTriangle, RefreshCw, Search, DollarSign, TrendingUp, TrendingDown, Cpu, Beaker, Trash, Sparkles, RotateCw, Clock, Eraser, MessageSquare, Inbox, Check, X, Bug, Lightbulb, HeartHandshake, Download, BarChart3, FlaskConical, Gavel, Shield, Mail, Send } from "lucide-react";

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const [activeTab, setActiveTab] = useState("overview");

  const load = async (q = "") => {
    setRefreshing(true);
    try {
      const [ov, us] = await Promise.all([
        api.get("/admin/overview"),
        api.get(`/admin/users?limit=200${q ? `&search=${encodeURIComponent(q)}` : ""}`),
      ]);
      setOverview(ov.data);
      setUsers(us.data.items);
      setError(null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setError("forbidden");
      } else {
        setError(err?.response?.data?.detail || "Failed to load admin data.");
        toast.error("Admin load failed.");
      }
    } finally { setRefreshing(false); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/auth"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  if (error === "forbidden") {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-6 md:px-12 py-20" data-testid="admin-forbidden">
          <div className="panel p-10 text-center corner-marks relative">
            <AlertTriangle className="w-10 h-10 text-destructive mx-auto mb-6" strokeWidth={1.5} />
            <div className="terminal-label text-destructive mb-3">[ ACCESS.DENIED ]</div>
            <h1 className="font-display text-2xl uppercase font-bold mb-3">Not authorized</h1>
            <p className="text-sm text-muted-foreground mb-6">This area is only for the Sisyphean team.</p>
            <Link to="/dashboard" className="btn-primary">Back to dashboard</Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10" data-testid="admin-page">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="terminal-label text-primary mb-2">[ ADMIN.OVERVIEW ]</div>
            <h1 className="font-display text-4xl uppercase font-bold tracking-tight">Admin</h1>
            <p className="text-xs text-muted-foreground mt-2">
              Creator-only view. Signed in as <span className="font-mono text-foreground">{user?.email}</span>.
            </p>
          </div>
          <button
            data-testid="admin-refresh"
            onClick={() => load(search)}
            disabled={refreshing}
            className="btn-secondary text-[10px] inline-flex items-center gap-2"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {!overview ? (
          <div className="p-12 text-center text-muted-foreground text-xs uppercase tracking-[0.3em]">LOADING<span className="blink-caret"/></div>
        ) : (
          <>
            <TabBar active={activeTab} onChange={setActiveTab} />

            {activeTab === "overview" && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-10">
                  <Stat label="Users" value={overview.users.total} sub="all-time" testId="stat-users-total" icon={Users} />
                  <Stat label="Verified" value={overview.users.verified} sub={`${pct(overview.users.verified, overview.users.total)}%`} testId="stat-users-verified" />
                  <Stat label="Signups · 24h" value={overview.users.last_24h} sub="last 24 hours" testId="stat-users-24h" />
                  <Stat label="Signups · 7d" value={overview.users.last_7d} sub="last 7 days" testId="stat-users-7d" />
                  <Stat label="Profiles complete" value={overview.users.profiles_complete} sub={`${pct(overview.users.profiles_complete, overview.users.total)}% of users`} testId="stat-profiles-complete" />
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-12">
                  <Stat label="Applications" value={overview.applications.total} sub="all generated" testId="stat-apps-total" icon={FileText} />
                  <Stat label="Materials ready" value={overview.applications.materials_ready} sub="AI generation done" testId="stat-apps-ready" />
                  <Stat label="Applications sent" value={overview.applications.sent} sub="user clicked send" testId="stat-apps-sent" icon={Briefcase} />
                </div>

                <MoneyPanel money={overview.money} users={overview.users} />
              </>
            )}

            {activeTab === "users" && (
              <>
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                  <div className="terminal-label">[ USERS.LIST ]</div>
                  <form
                    onSubmit={(e) => { e.preventDefault(); load(search); }}
                    className="flex items-center gap-2"
                  >
                    <div className="relative">
                      <Search className="w-3 h-3 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        data-testid="admin-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="search by email"
                        className="input-base pl-8 text-xs"
                        style={{ minWidth: 220 }}
                      />
                    </div>
                    <button type="submit" className="btn-secondary text-[10px]">Search</button>
                  </form>
                </div>

                <div className="panel overflow-x-auto" data-testid="admin-users-table">
                  <table className="w-full text-xs">
                    <thead className="bg-surface">
                      <tr className="text-left">
                        <Th>Email</Th>
                        <Th>Signed up</Th>
                        <Th>Verified</Th>
                        <Th>Profile</Th>
                        <Th>Access</Th>
                        <Th>Trial / sub</Th>
                        <Th>Action</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {users === null && (
                        <tr><td colSpan={7} className="p-8 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>
                      )}
                      {users?.length === 0 && (
                        <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No users match.</td></tr>
                      )}
                      {users?.map((u) => (
                        <tr key={u.id} className="border-t border-border hover:bg-surface/40" data-testid={`admin-user-${u.email}`}>
                          <Td className="font-mono">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span>{u.email}</span>
                              {u.is_admin && <Pill color="primary" label="ADMIN" testId={`admin-pill-${u.email}`} />}
                              {!u.is_admin && u.granted_access && <Pill color="green" label="GRANTED" testId={`granted-pill-${u.email}`} />}
                              {(u.ban_count > 0 || u.suspend_count > 0) && (
                                <span
                                  className="inline-flex items-center gap-1 text-[9px] tracking-[0.22em] uppercase font-mono px-1.5 py-0.5 border border-foreground/25 text-muted-foreground"
                                  title={`Lifetime · ${u.ban_count} ban${u.ban_count === 1 ? "" : "s"} · ${u.suspend_count} suspension${u.suspend_count === 1 ? "" : "s"}`}
                                  data-testid={`mod-tally-${u.email}`}
                                >
                                  {u.ban_count > 0 && <span className="text-destructive">B:{u.ban_count}</span>}
                                  {u.ban_count > 0 && u.suspend_count > 0 && <span className="text-foreground/30">·</span>}
                                  {u.suspend_count > 0 && <span className="text-amber-500">S:{u.suspend_count}</span>}
                                </span>
                              )}
                            </div>
                          </Td>
                          <Td className="text-muted-foreground">{fmtDate(u.created_at)}</Td>
                          <Td>{u.email_verified ? <Dot color="green" label="verified" /> : <Dot color="amber" label="pending" />}</Td>
                          <Td>{u.onboarded ? <Dot color="green" label="complete" /> : <Dot color="muted" label="setup" />}</Td>
                          <Td>
                            {u.status === "banned" ? (
                              <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-mono text-destructive" title={u.action_reason || ""} data-testid={`status-banned-${u.email}`}>
                                <Gavel className="w-3 h-3" /> Banned
                              </span>
                            ) : u.status === "suspended" ? (
                              <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-mono text-amber-500" title={u.action_reason || ""} data-testid={`status-suspended-${u.email}`}>
                                <Clock className="w-3 h-3" />
                                Suspended{u.suspended_until ? ` · ${fmtDate(u.suspended_until)}` : ""}
                              </span>
                            ) : u.has_active_access ? (
                              <Dot color="green" label="active" />
                            ) : (
                              <Dot color="red" label="expired" />
                            )}
                          </Td>
                          <Td className="text-muted-foreground">
                            {u.is_admin
                              ? " - "
                              : u.granted_access
                              ? "free · indefinite"
                              : u.subscription_ends_at
                              ? `sub: ${fmtDate(u.subscription_ends_at)}`
                              : `trial: ${fmtDate(u.trial_ends_at)}`}
                          </Td>
                          <Td>
                            <AccessButton user={u} onChange={() => load(search)} />
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {overview && (
                  <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono">
                    Generated at {fmtDate(overview.generated_at, true)} · showing newest first
                  </p>
                )}
              </>
            )}

            {activeTab === "feedback" && <FeedbackPanel />}

            {activeTab === "appeals" && <AppealsPanel />}

            {activeTab === "contributors" && <ContributorsPanel />}

            {activeTab === "waitlist" && <WaitlistPanel />}

            {activeTab === "broadcast" && <BroadcastPanel />}

            {activeTab === "testmode" && <TestModePanel onChanged={() => load(search)} />}
          </>
        )}
      </div>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Tab bar
// ---------------------------------------------------------------------------
function TabBar({ active, onChange }) {
  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3, testid: "admin-tab-overview" },
    { id: "users", label: "Users", icon: Users, testid: "admin-tab-users" },
    { id: "appeals", label: "Appeals", icon: Gavel, testid: "admin-tab-appeals" },
    { id: "feedback", label: "Feedback", icon: MessageSquare, testid: "admin-tab-feedback" },
    { id: "contributors", label: "Contributors", icon: HeartHandshake, testid: "admin-tab-contributors" },
    { id: "waitlist", label: "Waitlist", icon: Mail, testid: "admin-tab-waitlist" },
    { id: "broadcast", label: "Broadcast", icon: Send, testid: "admin-tab-broadcast" },
    { id: "testmode", label: "Test mode", icon: FlaskConical, testid: "admin-tab-testmode" },
  ];
  return (
    <div className="flex flex-wrap gap-1 mb-8 border-b border-foreground/20" data-testid="admin-tabbar">
      {tabs.map((t) => {
        const on = active === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            data-testid={t.testid}
            data-active={on}
            className={`px-4 py-2 -mb-px border-b-2 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-mono transition-colors ${
              on
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <t.icon className="w-3 h-3" strokeWidth={1.75} />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contributors panel (join-the-team submissions)
// ---------------------------------------------------------------------------
function ContributorsPanel() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/contribute");
      setItems(r.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load submissions.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      const fd = new FormData();
      fd.append("status", status);
      await api.patch(`/admin/contribute/${id}/status`, fd);
      toast.success(`Marked as ${status}.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update status.");
    }
  };

  const downloadFile = async (id, kind) => {
    try {
      const r = await api.get(`/admin/contribute/${id}/file/${kind}`, { responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : (kind === "snapshot" ? "snapshot.pdf" : "resume");
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to download file.");
    }
  };

  const filtered = (items || []).filter((it) => filter === "all" || it.status === filter);
  const counts = (items || []).reduce((acc, it) => { acc[it.status || "new"] = (acc[it.status || "new"] || 0) + 1; return acc; }, {});

  return (
    <div data-testid="admin-contributors-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="terminal-label">[ CONTRIBUTORS ]</div>
          <p className="text-xs text-muted-foreground mt-1">Inbound notes from <span className="font-mono">/join</span>. Newest first.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {["all", "new", "reviewed", "contacted", "archived"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              data-testid={`contrib-filter-${s}`}
              data-active={filter === s}
              className={`px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-mono border ${filter === s ? "border-primary text-primary" : "border-foreground/20 text-muted-foreground hover:text-foreground"}`}
            >
              {s} {s !== "all" && counts[s] ? `(${counts[s]})` : ""}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-muted-foreground text-xs uppercase tracking-[0.3em]"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
      ) : filtered.length === 0 ? (
        <div className="panel p-10 text-center text-muted-foreground" data-testid="contrib-empty">
          <Inbox className="w-8 h-8 mx-auto text-foreground/30 mb-3" strokeWidth={1.25} />
          <p className="text-sm">No submissions yet.</p>
        </div>
      ) : (
        <div className="panel" data-testid="contrib-list">
          {filtered.map((s) => {
            const open = expanded === s.id;
            const isFromProfile = s.submission_type === "from_profile";
            return (
              <div key={s.id} className="border-b border-border last:border-b-0" data-testid={`contrib-row-${s.id}`}>
                <button
                  type="button"
                  className="w-full text-left p-4 hover:bg-surface/40 flex items-start justify-between gap-4"
                  onClick={() => setExpanded(open ? null : s.id)}
                  data-testid={`contrib-toggle-${s.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-xs">{s.name || "(no name)"}</span>
                      <span className="text-muted-foreground text-xs">&lt;{s.email}&gt;</span>
                      {isFromProfile && <Pill color="primary" label="FROM PROFILE" />}
                      {s.resume_path && <Pill color="green" label="RESUME" />}
                      <StatusPill status={s.status || "new"} />
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-1">{s.message}</p>
                  </div>
                  <span className="text-[10px] text-muted-foreground font-mono whitespace-nowrap">{fmtDate(s.created_at, true)}</span>
                </button>
                {open && (
                  <div className="p-5 bg-surface/30 border-t border-border space-y-4" data-testid={`contrib-detail-${s.id}`}>
                    <div>
                      <div className="terminal-label mb-2">Message</div>
                      <p className="text-sm whitespace-pre-wrap leading-relaxed text-foreground/90">{s.message}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {isFromProfile && s.profile_snapshot_path && (
                        <button
                          type="button"
                          onClick={() => downloadFile(s.id, "snapshot")}
                          className="btn-secondary text-[10px] inline-flex items-center gap-2"
                          data-testid={`contrib-download-snapshot-${s.id}`}
                        >
                          <Download className="w-3 h-3" /> Profile snapshot (PDF)
                        </button>
                      )}
                      {s.resume_path && (
                        <button
                          type="button"
                          onClick={() => downloadFile(s.id, "resume")}
                          className="btn-secondary text-[10px] inline-flex items-center gap-2"
                          data-testid={`contrib-download-resume-${s.id}`}
                        >
                          <Download className="w-3 h-3" /> Resume
                        </button>
                      )}
                      <a
                        href={`mailto:${s.email}?subject=Re: Sisyphean`}
                        className="btn-secondary text-[10px] inline-flex items-center gap-2"
                        data-testid={`contrib-reply-${s.id}`}
                      >
                        <MessageSquare className="w-3 h-3" /> Reply
                      </a>
                      <div className="flex-1" />
                      {["new", "reviewed", "contacted", "archived"].map((st) => (
                        <button
                          key={st}
                          type="button"
                          onClick={() => updateStatus(s.id, st)}
                          disabled={s.status === st}
                          data-testid={`contrib-status-${st}-${s.id}`}
                          className="text-[10px] tracking-[0.2em] uppercase font-mono px-2 py-1 border border-foreground/20 hover:border-primary hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {st}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    new: { color: "amber", label: "NEW" },
    reviewed: { color: "muted", label: "REVIEWED" },
    contacted: { color: "green", label: "CONTACTED" },
    archived: { color: "muted", label: "ARCHIVED" },
  };
  const cfg = map[status] || map.new;
  return <Pill color={cfg.color} label={cfg.label} />;
}

// ---------------------------------------------------------------------------
// Broadcast panel - compose, preview, send marketing/newsletter emails
// ---------------------------------------------------------------------------
function BroadcastPanel() {
  const CATEGORIES = [
    { key: "announcements", label: "Announcements" },
    { key: "feature_updates", label: "Feature updates" },
    { key: "weekly_digest", label: "Weekly digest" },
    { key: "tips_and_guides", label: "Tips and guides" },
  ];
  const AUDIENCES = [
    { key: "all", label: "All users" },
    { key: "founders", label: "Founders only" },
    { key: "monthly", label: "Monthly subs only" },
    { key: "trial", label: "Free trial only" },
  ];
  const TONES = [
    { key: "warm-founder", label: "Warm founder" },
    { key: "punchy", label: "Punchy" },
    { key: "formal", label: "Formal" },
  ];
  const [subTab, setSubTab] = useState("compose"); // compose | templates
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("announcements");
  const [audience, setAudience] = useState("all");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  // AI generator
  const [aiTopic, setAiTopic] = useState("");
  const [aiTone, setAiTone] = useState("warm-founder");
  const [aiBusy, setAiBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  const loadHistory = async () => {
    try {
      const r = await api.get("/admin/broadcast/history");
      setHistory(r.data.broadcasts || []);
    } catch { /* ignore */ }
  };
  const loadSuggestions = async () => {
    try {
      const r = await api.get("/admin/broadcast/topic-suggestions");
      setSuggestions(r.data.suggestions || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadHistory(); loadSuggestions(); }, []);

  const runPreview = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Subject and body required.");
    setBusy(true);
    try {
      const r = await api.post("/admin/broadcast/preview", {
        subject: subject.trim(), body_markdown: body, category, audience,
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed.");
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!preview) return toast.error("Preview first.");
    if (!window.confirm(`Send to ~${preview.recipients_estimate} recipients now? This can't be undone.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/broadcast/send", {
        subject: subject.trim(), body_markdown: body, category, audience,
      });
      toast.success(r.data.message || "Broadcast queued.");
      setSubject(""); setBody(""); setPreview(null);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed.");
    } finally {
      setBusy(false);
    }
  };

  const generateWithAI = async (topicOverride) => {
    const topic = (topicOverride ?? aiTopic).trim();
    if (!topic) return toast.error("Type a topic first, or pick a suggestion.");
    setAiBusy(true);
    try {
      const r = await api.post("/admin/broadcast/generate", {
        topic, category, audience, tone: aiTone,
      });
      if (r.data.subject) setSubject(r.data.subject);
      if (r.data.body_markdown) setBody(r.data.body_markdown);
      setPreview(null);
      if (r.data.warning) toast.warning(r.data.warning);
      else toast.success("Newsletter drafted. Edit and preview before sending.");
      setAiTopic(topic);
      setSuggestionsOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed.");
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-broadcast-panel">
      <div>
        <div className="terminal-label text-primary">[ BROADCAST.EMAIL ]</div>
        <h2 className="font-display text-2xl uppercase font-semibold tracking-tight mt-1">Email center</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Compose newsletters or edit any automatic email the system sends. Every marketing email honors the recipient&apos;s preferences and includes an unsubscribe link.
        </p>
      </div>

      <div className="flex gap-2 border-b border-foreground/15 pb-0.5">
        <button
          onClick={() => setSubTab("compose")}
          data-testid="broadcast-subtab-compose"
          className={`px-3 py-2 text-[11px] tracking-[0.22em] uppercase font-mono transition-colors -mb-px border-b-2 ${subTab === "compose" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
        >Compose newsletter</button>
        <button
          onClick={() => setSubTab("templates")}
          data-testid="broadcast-subtab-templates"
          className={`px-3 py-2 text-[11px] tracking-[0.22em] uppercase font-mono transition-colors -mb-px border-b-2 ${subTab === "templates" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
        >Automatic emails</button>
      </div>

      {subTab === "templates" && <AutomaticEmailsPanel />}

      {subTab === "compose" && (<>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Compose */}
        <div className="panel p-5 space-y-4">
          {/* AI generator */}
          <div className="border border-primary/30 bg-primary/[0.03] p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="terminal-label text-primary inline-flex items-center gap-2">
                <Sparkles className="w-3 h-3" /> [ AI.DRAFT ]
              </div>
              <button
                type="button"
                onClick={() => setSuggestionsOpen((v) => !v)}
                data-testid="broadcast-ai-suggestions-toggle"
                className="text-[10px] uppercase tracking-[0.22em] font-mono text-muted-foreground hover:text-foreground"
              >{suggestionsOpen ? "Hide suggestions ▲" : "Suggestions ▼"}</button>
            </div>
            {suggestionsOpen && (
              <div className="mb-3 space-y-1.5 max-h-48 overflow-y-auto" data-testid="broadcast-ai-suggestions">
                {suggestions.length === 0 && (
                  <div className="text-[11px] font-mono text-muted-foreground/60">No suggestions loaded.</div>
                )}
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { setAiTopic(s.prompt); }}
                    data-testid={`broadcast-ai-suggestion-${i}`}
                    className="w-full text-left px-3 py-2 border border-foreground/15 hover:border-primary/50 hover:bg-primary/5 transition-colors"
                    title={s.prompt}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`text-[8px] px-1 py-0.5 uppercase tracking-[0.2em] font-mono ${s.source === 'metric' ? 'border border-primary/50 text-primary' : 'border border-foreground/20 text-muted-foreground'}`}>{s.source}</span>
                      <span className="text-[12px]">{s.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <textarea
              value={aiTopic}
              onChange={(e) => setAiTopic(e.target.value)}
              rows={2}
              className="input-base w-full text-sm mb-2"
              placeholder="What's this newsletter about? e.g., '1000 user milestone', 'Bookmarks + rank decay shipped', 'Founder tier 50% claimed'"
              data-testid="broadcast-ai-topic"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1">
                <span className="text-[9px] uppercase tracking-[0.22em] font-mono text-muted-foreground">Tone:</span>
                {TONES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setAiTone(t.key)}
                    data-testid={`broadcast-ai-tone-${t.key}`}
                    className={`px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-mono border transition-colors ${aiTone === t.key ? "border-primary text-primary bg-primary/5" : "border-foreground/20 text-muted-foreground hover:border-foreground/50"}`}
                  >{t.label}</button>
                ))}
              </div>
              <button
                onClick={() => generateWithAI()}
                disabled={aiBusy || !aiTopic.trim()}
                data-testid="broadcast-ai-generate"
                className="btn-primary text-[11px] inline-flex items-center gap-2 ml-auto"
              >
                {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Generate
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground/70 font-mono mt-2">
              Drafts a subject + body using Claude. You can edit before previewing/sending.
            </p>
          </div>

          <div>
            <label className="terminal-label mb-2 block">Category</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setCategory(c.key)}
                  data-testid={`broadcast-cat-${c.key}`}
                  className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-mono border transition-colors ${category === c.key ? "border-primary text-primary bg-primary/5" : "border-foreground/20 text-muted-foreground hover:border-foreground/50"}`}
                >{c.label}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="terminal-label mb-2 block">Audience</label>
            <div className="flex flex-wrap gap-2">
              {AUDIENCES.map((a) => (
                <button
                  key={a.key}
                  onClick={() => setAudience(a.key)}
                  data-testid={`broadcast-aud-${a.key}`}
                  className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-mono border transition-colors ${audience === a.key ? "border-primary text-primary bg-primary/5" : "border-foreground/20 text-muted-foreground hover:border-foreground/50"}`}
                >{a.label}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="terminal-label mb-2 block">Subject line</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="input-base w-full" placeholder="Sisyphean: what's new this week" data-testid="broadcast-subject" />
          </div>

          <div>
            <label className="terminal-label mb-2 block">Body (Markdown-lite: **bold**, *italic*, [links](url))</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              className="input-base w-full text-sm font-mono"
              placeholder="Hey friends,\n\nWe just shipped **X**. If you want to try it, [tap here](https://sisyphean.ai/dashboard).\n\n- Teddy"
              data-testid="broadcast-body"
            />
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <button onClick={runPreview} disabled={busy} className="btn-primary text-[11px]" data-testid="broadcast-preview-btn">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Preview"}
            </button>
            <button onClick={send} disabled={busy || !preview} className="btn-secondary text-[11px] inline-flex items-center gap-2" data-testid="broadcast-send-btn">
              <Send className="w-3 h-3" /> Send now
            </button>
            {preview && (
              <span className="text-[10px] uppercase tracking-[0.22em] font-mono text-muted-foreground ml-auto self-center">
                ~{preview.recipients_estimate} recipients ({preview.audience_total} in audience)
              </span>
            )}
          </div>
        </div>

        {/* Preview */}
        <div className="panel p-5 min-h-[400px]">
          <div className="terminal-label mb-3">[ PREVIEW ]</div>
          {preview ? (
            <div
              className="border border-foreground/20 bg-white text-black p-1 max-h-[540px] overflow-y-auto"
              data-testid="broadcast-preview-html"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.html, { ADD_ATTR: ['target', 'style'] }) }}
            />
          ) : (
            <div className="text-[11px] uppercase tracking-[0.22em] font-mono text-muted-foreground/60 py-10 text-center">
              Fill in subject + body, hit Preview.
            </div>
          )}
        </div>
      </div>

      {/* History */}
      <div>
        <div className="terminal-label mb-3">[ RECENT.BROADCASTS ]</div>
        {history.length === 0 ? (
          <div className="text-[11px] uppercase tracking-[0.22em] font-mono text-muted-foreground/60 py-4">Nothing sent yet.</div>
        ) : (
          <div className="panel p-0 overflow-hidden">
            <table className="w-full text-[12px]" data-testid="broadcast-history">
              <thead>
                <tr className="border-b border-foreground/20 bg-surface">
                  <th className="text-left px-4 py-2 font-mono uppercase tracking-[0.18em] text-[10px] text-muted-foreground">Subject</th>
                  <th className="text-left px-4 py-2 font-mono uppercase tracking-[0.18em] text-[10px] text-muted-foreground">Category</th>
                  <th className="text-left px-4 py-2 font-mono uppercase tracking-[0.18em] text-[10px] text-muted-foreground">Audience</th>
                  <th className="text-left px-4 py-2 font-mono uppercase tracking-[0.18em] text-[10px] text-muted-foreground">Sent</th>
                  <th className="text-left px-4 py-2 font-mono uppercase tracking-[0.18em] text-[10px] text-muted-foreground">At</th>
                </tr>
              </thead>
              <tbody>
                {history.map((b) => (
                  <tr key={b.id} className="border-b border-foreground/10">
                    <td className="px-4 py-3">{b.subject}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">{b.category}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">{b.audience}</td>
                    <td className="px-4 py-3 font-mono">{b.sent_count}{b.skipped_count ? <span className="text-muted-foreground"> · {b.skipped_count} skipped</span> : null}{b.failed_count ? <span className="text-primary"> · {b.failed_count} failed</span> : null}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{b.created_at ? new Date(b.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>)}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Automatic emails - edit subject/body of any auto-triggered email
// ---------------------------------------------------------------------------
function AutomaticEmailsPanel() {
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyIntro, setBodyIntro] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/email-templates");
      const list = r.data.templates || [];
      setTemplates(list);
      if (list.length && !selectedKey) setSelectedKey(list[0].key);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load templates.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selected = templates.find((t) => t.key === selectedKey);

  useEffect(() => {
    if (!selected) return;
    setSubject(selected.subject || "");
    setBodyIntro(selected.body_intro || "");
    setBodyHtml(selected.body_html || "");
    setDirty(false);
  }, [selected?.key]); // eslint-disable-line

  const isTransactional = selected?.kind === "transactional";

  const save = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const body = { subject };
      if (isTransactional) body.body_intro = bodyIntro;
      else body.body_html = bodyHtml;
      await api.put(`/admin/email-templates/${selected.key}`, body);
      toast.success("Template saved.");
      setDirty(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!selected) return;
    if (!window.confirm(`Restore "${selected.label}" to its default? Your custom subject and body will be discarded.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/email-templates/${selected.key}/reset`);
      const r = await api.get("/admin/email-templates");
      const list = r.data.templates || [];
      setTemplates(list);
      const fresh = list.find((t) => t.key === selected.key);
      if (fresh) {
        setSubject(fresh.subject || "");
        setBodyIntro(fresh.body_intro || "");
        setBodyHtml(fresh.body_html || "");
      }
      setDirty(false);
      toast.success("Restored to default.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed.");
    } finally { setBusy(false); }
  };

  const testSend = async () => {
    if (!selected) return;
    if (dirty && !window.confirm("You have unsaved changes. Save first? (Cancel to send the last saved version.)")) return;
    if (dirty) await save();
    setBusy(true);
    try {
      const r = await api.post(`/admin/email-templates/${selected.key}/test-send`);
      toast.success(`Test sent to ${r.data.to}. Check your inbox.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed.");
    } finally { setBusy(false); }
  };

  const markDirty = () => { if (!dirty) setDirty(true); };

  return (
    <div className="space-y-4" data-testid="automatic-emails-panel">
      <div className="panel p-5 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="terminal-label">Template:</label>
          <select
            value={selectedKey}
            onChange={(e) => { setSelectedKey(e.target.value); setDirty(false); }}
            data-testid="template-select"
            className="input-base text-sm px-2 py-1.5 min-w-[300px]"
            disabled={loading}
          >
            {templates.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}{t.customized ? " · customized" : ""}
              </option>
            ))}
          </select>
          {selected?.customized && (
            <span className="text-[9px] uppercase tracking-[0.22em] font-mono border border-primary/50 text-primary px-1.5 py-0.5">customized</span>
          )}
        </div>

        {selected && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px]">
              <div>
                <div className="text-[9px] uppercase tracking-[0.22em] font-mono text-muted-foreground mb-1">Kind</div>
                <div className="font-mono">{selected.kind}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.22em] font-mono text-muted-foreground mb-1">Sent to</div>
                <div className="font-mono">{selected.recipient}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.22em] font-mono text-muted-foreground mb-1">Last updated</div>
                <div className="font-mono">{selected.updated_at ? new Date(selected.updated_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "never"}</div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">{selected.description}</p>

            {selected.placeholders?.length > 0 && (
              <div className="text-[11px] border border-foreground/15 bg-surface p-3">
                <div className="terminal-label mb-1.5">[ AVAILABLE.PLACEHOLDERS ]</div>
                <div className="flex flex-wrap gap-1.5">
                  {selected.placeholders.map((p) => (
                    <code key={p} className="font-mono text-[10px] px-1.5 py-0.5 bg-background border border-foreground/15">{`{${p}}`}</code>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground/70 mt-2">These get replaced at send time with real values.</p>
              </div>
            )}

            <div>
              <label className="terminal-label mb-2 block">Subject</label>
              <input
                value={subject}
                onChange={(e) => { setSubject(e.target.value); markDirty(); }}
                className="input-base w-full"
                data-testid="template-subject"
              />
              <p className="text-[10px] text-muted-foreground/60 mt-1 font-mono">Default: {selected.default_subject}</p>
            </div>

            {isTransactional ? (
              <div>
                <label className="terminal-label mb-2 block">Body intro (single paragraph shown above the CTA button)</label>
                <textarea
                  value={bodyIntro}
                  onChange={(e) => { setBodyIntro(e.target.value); markDirty(); }}
                  rows={4}
                  className="input-base w-full text-sm"
                  data-testid="template-body-intro"
                />
                <p className="text-[10px] text-muted-foreground/60 mt-1 font-mono">Default: {selected.default_body_intro}</p>
              </div>
            ) : (
              <div>
                <label className="terminal-label mb-2 block">Body (raw HTML — placeholders inside {`{curly}`})</label>
                <textarea
                  value={bodyHtml}
                  onChange={(e) => { setBodyHtml(e.target.value); markDirty(); }}
                  rows={14}
                  className="input-base w-full text-xs font-mono"
                  data-testid="template-body-html"
                />
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 pt-2">
              <button
                onClick={save}
                disabled={busy || !dirty}
                data-testid="template-save"
                className="btn-primary text-[11px]"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
              </button>
              <button
                onClick={testSend}
                disabled={busy}
                data-testid="template-test-send"
                className="btn-secondary text-[11px] inline-flex items-center gap-2"
              >
                <Send className="w-3 h-3" /> Send test to me
              </button>
              <button
                onClick={reset}
                disabled={busy || !selected.customized}
                data-testid="template-reset"
                className="btn-ghost text-[11px] inline-flex items-center gap-2 ml-auto"
              >
                <RotateCw className="w-3 h-3" /> Restore default
              </button>
            </div>
            {dirty && (
              <p className="text-[10px] uppercase tracking-[0.22em] font-mono text-accent-amber">
                Unsaved changes
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Waitlist panel - pre-launch coming-soon email captures
// ---------------------------------------------------------------------------
function WaitlistPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get("/admin/coming-soon/subscribers");
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load waitlist.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id, email) => {
    if (!window.confirm(`Remove ${email} from the waitlist? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/coming-soon/subscribers/${id}`);
      toast.success("Removed.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove.");
    }
  };

  const exportCsv = () => {
    if (!data?.subscribers?.length) return;
    const header = "email,created_at,ip,user_agent\n";
    const rows = data.subscribers.map((s) => {
      const ua = (s.user_agent || "").replace(/"/g, '""');
      return `"${s.email}","${s.created_at}","${s.ip || ""}","${ua}"`;
    }).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sisyphean-waitlist-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground py-12"><Loader2 className="w-4 h-4 animate-spin" /> Loading waitlist...</div>;
  if (error) return <div className="panel border-primary p-4 text-sm" data-testid="waitlist-error"><AlertTriangle className="w-4 h-4 inline mr-2" />{error}</div>;

  const subs = data?.subscribers || [];
  const total = data?.total || 0;

  return (
    <div className="space-y-6" data-testid="admin-waitlist-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="terminal-label text-primary">[ PRE-LAUNCH.WAITLIST ]</div>
          <h2 className="font-display text-2xl uppercase font-semibold tracking-tight mt-1">Email list</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Emails captured from the coming-soon holding page. Separate from your real user signups.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="terminal-label text-muted-foreground">[ {total} ON LIST ]</span>
          <button
            onClick={exportCsv}
            disabled={!subs.length}
            className="btn-ghost text-[11px] inline-flex items-center gap-1.5"
            data-testid="waitlist-export-csv"
          >
            <Download className="w-3 h-3" /> Export CSV
          </button>
          <button onClick={load} className="btn-ghost text-[11px] inline-flex items-center gap-1.5" data-testid="waitlist-refresh">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {subs.length === 0 ? (
        <div className="panel p-8 text-center" data-testid="waitlist-empty">
          <Inbox className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">Nobody on the list yet.</p>
          <p className="text-[11px] uppercase tracking-[0.22em] font-mono text-muted-foreground/60 mt-1">
            Subscriptions arrive here as soon as a visitor enters their email on the holding page.
          </p>
        </div>
      ) : (
        <div className="panel p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="waitlist-table">
              <thead>
                <tr className="border-b border-foreground/20 bg-surface">
                  <th className="text-left px-4 py-2 font-mono uppercase tracking-[0.18em] text-[10px] text-muted-foreground">Email</th>
                  <th className="text-left px-4 py-2 font-mono uppercase tracking-[0.18em] text-[10px] text-muted-foreground">Joined</th>
                  <th className="text-left px-4 py-2 font-mono uppercase tracking-[0.18em] text-[10px] text-muted-foreground hidden md:table-cell">IP</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.id} className="border-b border-foreground/10 hover:bg-surface/50" data-testid={`waitlist-row-${s.id}`}>
                    <td className="px-4 py-3 font-mono break-all">{s.email}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                      {s.created_at ? new Date(s.created_at).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "-"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground font-mono text-[11px] hidden md:table-cell">{s.ip || "-"}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => remove(s.id, s.email)}
                        className="text-muted-foreground hover:text-primary p-1"
                        title="Remove from waitlist"
                        data-testid={`waitlist-delete-${s.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function FeedbackPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [insights, setInsights] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [fb, ins] = await Promise.all([
        api.get("/admin/feedback"),
        api.get("/admin/help-insights").catch(() => ({ data: null })),
      ]);
      setData(fb.data);
      setInsights(ins.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't load feedback.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resolve = async (id) => {
    try {
      await api.post(`/admin/feedback/${id}/resolve`);
      toast.success("Marked resolved.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't resolve.");
    }
  };

  if (loading) {
    return (
      <div className="mb-12" data-testid="admin-feedback-panel">
        <div className="terminal-label text-primary mb-1 inline-flex items-center gap-2"><Inbox className="w-3.5 h-3.5" /> [ INBOX ]</div>
        <div className="panel p-6 text-center text-muted-foreground text-xs"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading feedback…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-12 panel p-4 border-destructive/40 text-destructive text-sm font-mono">{error}</div>
    );
  }

  const items = data?.items || [];
  const clusters = data?.clusters || [];
  const counts = data?.counts || {};
  const themes = insights?.themes || [];

  return (
    <div className="mb-12" data-testid="admin-feedback-panel">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="terminal-label text-primary inline-flex items-center gap-2">
            <Inbox className="w-3.5 h-3.5" strokeWidth={1.5} /> [ INBOX ]
          </div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono mt-1">
            user feedback · auto-classified by priority + category · ai-clustered
          </p>
        </div>
        <button onClick={load} className="text-[10px] uppercase tracking-[0.2em] font-mono px-3 py-1.5 border border-foreground/40 hover:bg-foreground hover:text-background">refresh</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
        <PriCount label="P0 · Critical" value={counts.by_priority?.P0 || 0} color="text-destructive" />
        <PriCount label="P1 · High" value={counts.by_priority?.P1 || 0} color="text-accent-amber" />
        <PriCount label="P2 · Medium" value={counts.by_priority?.P2 || 0} color="text-foreground" />
        <PriCount label="P3 · Low" value={counts.by_priority?.P3 || 0} color="text-muted-foreground" />
        <PriCount label="OPEN" value={counts.open || 0} color="text-accent-green" />
      </div>

      {clusters.length > 0 && (
        <div className="panel p-5 mb-3" data-testid="admin-feedback-clusters">
          <div className="terminal-label mb-3">[ THEMES.IN.OPEN.FEEDBACK ]</div>
          <div className="space-y-3">
            {clusters.slice(0, 8).map((c, i) => (
              <div key={i} className="border-l-2 border-primary pl-3" data-testid={`feedback-cluster-${i}`}>
                <div className="text-sm font-mono font-medium">{c.theme} <span className="text-muted-foreground/70 text-xs">· {c.item_ids?.length || 0} item{c.item_ids?.length === 1 ? "" : "s"}</span></div>
                {c.suggested_fix && <div className="text-xs text-muted-foreground leading-relaxed mt-1 italic">→ {c.suggested_fix}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {themes.length > 0 && (
        <div className="panel p-5 mb-3" data-testid="admin-help-themes">
          <div className="terminal-label mb-3">[ FREQUENT.QUESTIONS · LAST.14D ]</div>
          <div className="space-y-3">
            {themes.slice(0, 6).map((t, i) => (
              <div key={i} className="border-l-2 border-accent-amber pl-3">
                <div className="text-sm font-mono font-medium">{t.theme} <span className="text-muted-foreground/70 text-xs">· {t.count}× asked · {t.page || "various"}</span></div>
                {t.sample_question && <div className="text-xs text-muted-foreground italic mt-1">e.g. "{t.sample_question.slice(0, 200)}"</div>}
                {t.suggested_fix && <div className="text-xs text-foreground/80 mt-1">→ {t.suggested_fix}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="panel overflow-x-auto" data-testid="admin-feedback-list">
        <table className="w-full text-xs">
          <thead className="bg-surface">
            <tr className="text-left">
              <Th>Pri</Th>
              <Th>Type</Th>
              <Th>Category</Th>
              <Th>Summary</Th>
              <Th>From</Th>
              <Th>Page</Th>
              <Th>When</Th>
              <Th>Action</Th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No feedback yet.</td></tr>
            )}
            {items.map((f) => {
              const isOpen = f.status === "open";
              const TypeIcon = f.type === "bug" ? Bug : f.type === "suggestion" ? Lightbulb : MessageSquare;
              return (
                <tr key={f.id} className={`border-t border-border ${isOpen ? "" : "opacity-50"}`} data-testid={`feedback-row-${f.id}`}>
                  <Td><PriPill p={f.priority} /></Td>
                  <Td><span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-mono"><TypeIcon className="w-3 h-3" />{f.type}</span></Td>
                  <Td className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">{f.category}</Td>
                  <Td className="max-w-md">
                    <div className="font-medium">{f.summary}</div>
                    <div className="text-muted-foreground text-[11px] mt-0.5 leading-relaxed line-clamp-3 whitespace-pre-line">{f.text}</div>
                  </Td>
                  <Td className="text-muted-foreground">{f.email || "anon"}</Td>
                  <Td className="font-mono text-[10px] text-muted-foreground">{f.page_path || "-"}</Td>
                  <Td className="font-mono text-[10px] text-muted-foreground">{fmtDate(f.created_at)}</Td>
                  <Td>
                    {isOpen ? (
                      <button data-testid={`feedback-resolve-${f.id}`} onClick={() => resolve(f.id)} className="text-[10px] uppercase tracking-[0.18em] font-mono px-2 py-1 border border-accent-green/60 text-accent-green hover:bg-accent-green hover:text-background">
                        <Check className="w-3 h-3 inline" /> Resolve
                      </button>
                    ) : (
                      <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 font-mono">resolved</span>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PriCount({ label, value, color }) {
  return (
    <div className="panel p-3 text-center">
      <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground font-mono mb-1">{label}</div>
      <div className={`serif-mod text-3xl tracking-tight ${color}`} style={{ fontWeight: 500 }}>{value}</div>
    </div>
  );
}

function PriPill({ p }) {
  const map = {
    P0: "border-destructive bg-destructive/15 text-destructive",
    P1: "border-accent-amber/60 bg-accent-amber/10 text-accent-amber",
    P2: "border-foreground/30 text-foreground",
    P3: "border-muted-foreground/40 text-muted-foreground",
  };
  return <span className={`inline-block text-[10px] uppercase tracking-[0.2em] font-mono px-1.5 py-0.5 border ${map[p] || map.P2}`}>{p}</span>;
}


function TestModePanel({ onChanged }) {
  const [busy, setBusy] = useState(null);
  const [hint, setHint] = useState("");
  const [jobCount, setJobCount] = useState(5);
  const [trialDays, setTrialDays] = useState(7);
  const [currentPersona, setCurrentPersona] = useState(null);

  const loadPersona = async () => {
    try {
      const r = await api.get("/profile");
      setCurrentPersona(r.data?.name ? { name: r.data.name, title: r.data.title, location: r.data.location } : null);
    } catch { setCurrentPersona(null); }
  };

  useEffect(() => { loadPersona(); }, []);

  const run = async (action, fn, successMsg) => {
    setBusy(action);
    try {
      const res = await fn();
      toast.success(successMsg(res?.data));
      onChanged?.();
      return res?.data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That didn't work.");
    } finally { setBusy(null); }
  };

  const seedPersona = async () => {
    const data = await run(
      "gen-persona",
      () => api.post("/admin/test/generate-persona", { mode: "random", hint: hint.trim() || null }),
      (d) => `Seeded "${d?.name}" · ${d?.title} · ${d?.location}.`,
    );
    if (data) await loadPersona();
  };

  const generateJobs = () => run(
    "gen-jobs",
    () => api.post("/admin/test/generate-jobs-for-persona", { count: Number(jobCount) || 5 }),
    (d) => {
      const counts = (d?.jobs || []).reduce((acc, j) => { acc[j.fit_level] = (acc[j.fit_level] || 0) + 1; return acc; }, {});
      const breakdown = Object.entries(counts).map(([k, v]) => `${v} ${k}`).join(" · ");
      return `Generated ${d?.inserted || 0} jobs · ${breakdown || "queued"}.`;
    },
  );

  const wipe = async () => {
    const data = await run("wipe", () => api.post("/admin/test/wipe-profile"),
      (d) => `Wiped · ${d?.deleted_applications || 0} apps, ${d?.deleted_custom_jobs || 0} test jobs cleared.`);
    if (data) await loadPersona();
  };
  const clearApps = () => run("clear", () => api.post("/admin/test/clear-applications"),
    (d) => `Cleared ${d?.deleted || 0} generated applications.`);
  const resetTrial = () => run("trial", () => api.post(`/admin/test/reset-trial?days=${Number(trialDays) || 0}`),
    (d) => `Trial ends ${new Date(d?.trial_ends_at).toLocaleDateString()}.`);

  const hasPersona = !!currentPersona;

  return (
    <div className="mb-12" data-testid="admin-test-panel">
      <div className="mb-3">
        <div className="terminal-label text-primary inline-flex items-center gap-2">
          <Beaker className="w-3.5 h-3.5" strokeWidth={1.5} /> [ TEST.MODE ]
        </div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono mt-1">
          mirror the real app · seed a persona, then generate jobs that partially fit
        </p>
      </div>

      {/* STEP 1 - Persona */}
      <div className="panel p-5 mb-3" data-testid="test-step-persona">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="terminal-label text-primary mb-1">[ STEP 1 ] · Seed a persona</div>
            <p className="text-xs text-muted-foreground leading-relaxed max-w-lg">
              Generates a random, realistic creative professional with a full career history and a mixed portfolio. Becomes your test profile.
            </p>
          </div>
          {hasPersona && (
            <div className="text-right" data-testid="current-persona">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono mb-1">Current persona</div>
              <div className="text-sm font-mono text-foreground">{currentPersona.name}</div>
              <div className="text-[11px] text-muted-foreground">{currentPersona.title} · {currentPersona.location}</div>
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap items-stretch mt-3">
          <input
            data-testid="test-hint-input"
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            disabled={!!busy}
            placeholder='Persona hint (optional) - e.g., "mid-career illustrator with side gigs"'
            className="input-base text-xs flex-1 min-w-[260px]"
          />
          <button
            data-testid="test-generate-persona-btn"
            onClick={seedPersona}
            disabled={!!busy}
            className="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
          >
            {busy === "gen-persona"
              ? <><Loader2 className="w-3 h-3 animate-spin" /> Seeding…</>
              : <><Sparkles className="w-3 h-3" /> {hasPersona ? "Re-seed persona" : "Seed persona"} →</>}
          </button>
        </div>
      </div>

      {/* STEP 2 - Jobs for persona */}
      <div className="panel p-5 mb-3" data-testid="test-step-jobs">
        <div className="terminal-label text-primary mb-1">[ STEP 2 ] · Generate jobs for this persona</div>
        <p className="text-xs text-muted-foreground leading-relaxed max-w-xl mb-3">
          Creates a feed of jobs that partially overlap with the persona's skills - a realistic mix of <span className="text-accent-green">strong</span>, <span className="text-accent-amber">moderate</span>, and stretch fits, exactly how a real user's feed looks. Each lands in your feed already-scored. Click a row to run the ATS A/B test.
        </p>
        <div className="flex gap-2 flex-wrap items-stretch">
          <select
            data-testid="test-job-count"
            value={jobCount}
            onChange={(e) => setJobCount(e.target.value)}
            disabled={!!busy || !hasPersona}
            className="input-base text-xs"
          >
            <option value={3}>3 jobs</option>
            <option value={5}>5 jobs</option>
            <option value={6}>6 jobs</option>
          </select>
          <button
            data-testid="test-generate-jobs-btn"
            onClick={generateJobs}
            disabled={!!busy || !hasPersona}
            className="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
            title={hasPersona ? "" : "Seed a persona first"}
          >
            {busy === "gen-jobs"
              ? <><Loader2 className="w-3 h-3 animate-spin" /> Generating feed…</>
              : <><Briefcase className="w-3 h-3" /> Generate jobs →</>}
          </button>
        </div>
        {!hasPersona && (
          <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70 font-mono mt-3">
            ⤴ seed a persona first
          </p>
        )}
      </div>

      {/* Utilities row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <TestCard
          icon={Eraser}
          label="Wipe my profile"
          desc="Reset profile to blank · clear my applications + any test jobs I created."
          actionLabel={busy === "wipe" ? "Wiping…" : "Wipe →"}
          onAction={wipe}
          disabled={!!busy}
          tone="destructive"
          testId="test-wipe-profile"
        />
        <TestCard
          icon={RotateCw}
          label="Clear my applications"
          desc="Wipe my user_jobs so I can re-run scoring + materials on the same job rows."
          actionLabel={busy === "clear" ? "Clearing…" : "Clear →"}
          onAction={clearApps}
          disabled={!!busy}
          tone="destructive"
          testId="test-clear-apps"
        />
        <TestCard
          icon={Clock}
          label="Reset trial"
          desc="Flip my trial_ends_at to test trial-expired UX. Negative days = already expired."
          actionLabel={busy === "trial" ? "…" : "Apply →"}
          onAction={resetTrial}
          disabled={!!busy}
          tone="primary"
          testId="test-reset-trial"
        >
          <input
            data-testid="test-trial-days"
            type="number"
            value={trialDays}
            onChange={(e) => setTrialDays(e.target.value)}
            disabled={!!busy}
            min={-30}
            max={60}
            className="input-base w-full text-xs mt-3"
          />
        </TestCard>
      </div>
    </div>
  );
}



function TestCard({ icon: Icon, label, desc, actionLabel, onAction, disabled, tone, testId, children }) {
  const toneClass = tone === "destructive"
    ? "border-destructive/60 text-destructive hover:bg-destructive hover:text-destructive-foreground"
    : "border-foreground/40 text-foreground hover:bg-foreground hover:text-background";
  return (
    <div className="panel p-5 flex flex-col" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-3.5 h-3.5 text-foreground/60" strokeWidth={1.5} />
        <div className="terminal-label">{label}</div>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed mb-3 flex-1">{desc}</p>
      {children}
      <button
        onClick={onAction}
        disabled={disabled}
        className={`mt-4 self-start text-[10px] uppercase tracking-[0.2em] font-mono px-3 py-2 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${toneClass}`}
      >
        {actionLabel}
      </button>
    </div>
  );
}

function MoneyPanel({ money, users }) {
  const w = money?.all_time || {};
  const w7 = money?.last_7d || {};
  const w24 = money?.last_24h || {};
  const fmtUsd = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const NetPill = ({ net }) => {
    const positive = (net || 0) >= 0;
    const Icon = positive ? TrendingUp : TrendingDown;
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-mono ${
          positive ? "text-accent-green" : "text-destructive"
        }`}
      >
        <Icon className="w-3 h-3" strokeWidth={2} />
        {fmtUsd(net)}
      </span>
    );
  };
  const purposeRows = (w.llm?.by_purpose || []).slice(0, 10);
  return (
    <div className="mb-12" data-testid="admin-money-panel">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="terminal-label text-primary">[ MONEY.P&amp;L ]</div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono mt-1">
            revenue from Stripe · llm cost from estimated tokens · ~4 chars/token · claude sonnet 4.5 pricing
          </p>
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono">
          ${money?.subscription_price_usd}/mo · {users?.paying || 0} paying · {users?.active_trials || 0} on trial
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
        <MoneyCard label="All-time" data={w} testId="money-alltime" fmtUsd={fmtUsd} NetPill={NetPill} />
        <MoneyCard label="Last 7 days" data={w7} testId="money-7d" fmtUsd={fmtUsd} NetPill={NetPill} />
        <MoneyCard label="Last 24 hours" data={w24} testId="money-24h" fmtUsd={fmtUsd} NetPill={NetPill} />
      </div>

      {purposeRows.length > 0 && (
        <div className="panel overflow-x-auto" data-testid="admin-purpose-breakdown">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <Cpu className="w-3.5 h-3.5 text-foreground/60" strokeWidth={1.5} />
            <div className="terminal-label">LLM spend by purpose · all-time</div>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-surface/40">
              <tr className="text-left">
                <Th>Purpose</Th>
                <Th>Calls</Th>
                <Th>Input tokens</Th>
                <Th>Output tokens</Th>
                <Th>Cost</Th>
              </tr>
            </thead>
            <tbody>
              {purposeRows.map((row) => (
                <tr key={row.purpose} className="border-t border-border" data-testid={`money-purpose-${row.purpose}`}>
                  <Td className="font-mono">{row.purpose}</Td>
                  <Td className="text-muted-foreground">{row.calls.toLocaleString()}</Td>
                  <Td className="text-muted-foreground">{row.input_tokens.toLocaleString()}</Td>
                  <Td className="text-muted-foreground">{row.output_tokens.toLocaleString()}</Td>
                  <Td>{fmtUsd(row.cost_usd)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MoneyCard({ label, data, testId, fmtUsd, NetPill }) {
  const revenue = data?.revenue?.revenue_usd || 0;
  const llmCost = data?.llm?.cost_usd || 0;
  const net = data?.net_usd ?? (revenue - llmCost);
  const margin = data?.margin_pct;
  return (
    <div className="panel p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div className="terminal-label">{label}</div>
        <NetPill net={net} />
      </div>
      <div className="space-y-3">
        <Row icon={DollarSign} label="Revenue" value={fmtUsd(revenue)} sub={`${data?.revenue?.transactions || 0} txn`} positive />
        <Row icon={Cpu} label="LLM cost" value={fmtUsd(llmCost)} sub={`${(data?.llm?.calls || 0).toLocaleString()} calls · ${((data?.llm?.input_tokens || 0) + (data?.llm?.output_tokens || 0)).toLocaleString()} tok`} />
        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div className="terminal-label">Margin</div>
          <div className="font-mono text-xs">
            {margin === null || margin === undefined ? " - " : `${margin}%`}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, sub, positive }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <Icon className={`w-3.5 h-3.5 flex-none ${positive ? "text-accent-green" : "text-foreground/60"}`} strokeWidth={1.5} />
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-mono text-muted-foreground">{label}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 font-mono mt-0.5">{sub}</div>
        </div>
      </div>
      <div className={`serif-mod text-2xl tracking-[-0.02em] leading-none ${positive ? "text-foreground" : "text-foreground"}`} style={{ fontWeight: 500 }}>
        {value}
      </div>
    </div>
  );
}

function Stat({ label, value, sub, testId, icon: Icon }) {
  return (
    <div className="panel p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-3.5 h-3.5 text-foreground/60" strokeWidth={1.5} />}
        <div className="terminal-label">{label}</div>
      </div>
      <div className="serif-mod text-4xl tracking-[-0.03em] leading-none mb-1" style={{ fontWeight: 500 }}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">{sub}</div>
    </div>
  );
}

function Pill({ color, label, testId }) {
  const colorMap = {
    primary: "border-primary/50 text-primary",
    green: "border-accent-green/50 text-accent-green",
  };
  return (
    <span
      data-testid={testId}
      className={`text-[9px] uppercase tracking-[0.22em] font-mono px-1.5 py-0.5 border ${colorMap[color] || colorMap.green}`}
    >
      {label}
    </span>
  );
}

function AccessButton({ user, onChange }) {
  return <UserActionsMenu user={user} onChange={onChange} />;
}

function UserActionsMenu({ user, onChange }) {
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // 'suspend' | 'ban' | 'reinstate' | null
  const [reasons, setReasons] = useState({ suspend: [], ban: [] });

  useEffect(() => {
    api.get("/admin/moderation/reasons").then((r) => setReasons(r.data)).catch(() => {});
  }, []);

  if (user.is_admin) {
    return <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/60 font-mono"> - </span>;
  }

  const status = user.status || "active";
  const granted = !!user.granted_access;

  const toggleGrant = async () => {
    setBusy(true);
    try {
      const path = granted ? `/admin/users/${user.id}/revoke-access` : `/admin/users/${user.id}/grant-access`;
      await api.post(path);
      toast.success(granted ? `Revoked free access for ${user.email}` : `Granted free access to ${user.email}`);
      await onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {status === "active" ? (
        <>
          <ActionPill
            label={granted ? "Revoke" : "Grant"}
            color={granted ? "destructive" : "default"}
            onClick={toggleGrant}
            busy={busy}
            testId={granted ? `revoke-access-${user.email}` : `grant-access-${user.email}`}
          />
          <ActionPill label="Suspend" color="amber" onClick={() => setModal("suspend")} testId={`suspend-${user.email}`} />
          <ActionPill label="Ban" color="destructive" onClick={() => setModal("ban")} testId={`ban-${user.email}`} />
        </>
      ) : (
        <>
          <ActionPill
            label={`Reinstate`}
            color="green"
            onClick={() => setModal("reinstate")}
            testId={`reinstate-${user.email}`}
          />
        </>
      )}

      {modal === "suspend" && (
        <SuspendModal user={user} reasons={reasons.suspend || []} onClose={() => setModal(null)} onDone={() => { setModal(null); onChange?.(); }} />
      )}
      {modal === "ban" && (
        <BanModal user={user} reasons={reasons.ban || []} onClose={() => setModal(null)} onDone={() => { setModal(null); onChange?.(); }} />
      )}
      {modal === "reinstate" && (
        <ReinstateModal user={user} onClose={() => setModal(null)} onDone={() => { setModal(null); onChange?.(); }} />
      )}
    </div>
  );
}

function ActionPill({ label, color = "default", onClick, busy, testId }) {
  const colorMap = {
    default: "border-foreground/40 text-foreground hover:bg-foreground hover:text-background",
    destructive: "border-destructive/50 text-destructive hover:bg-destructive/10",
    amber: "border-amber-500/50 text-amber-500 hover:bg-amber-500/10",
    green: "border-accent-green/50 text-accent-green hover:bg-accent-green/10",
  };
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={busy}
      className={`text-[10px] uppercase tracking-[0.2em] font-mono px-2.5 py-1 border transition-colors disabled:opacity-50 ${colorMap[color] || colorMap.default}`}
    >
      {busy ? "…" : label}
    </button>
  );
}

// Modal scaffold
function ModalShell({ title, children, onClose, accent = "primary" }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4" onClick={onClose} data-testid="admin-modal">
      <div className="brut-tile bg-background w-full max-w-md p-5 sm:p-6" style={accent === "destructive" ? { borderColor: "hsl(var(--destructive))" } : {}} onClick={(e) => e.stopPropagation()}>
        <div className="terminal-label mb-4 text-primary">[ {title} ]</div>
        {children}
      </div>
    </div>
  );
}

function ReasonPicker({ reasons, value, onChange, customValue, onCustomChange }) {
  return (
    <div className="space-y-2">
      <label className="terminal-label block">Reason</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-base text-sm"
        data-testid="reason-select"
      >
        <option value="">- Select a reason -</option>
        {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      {value === "Other" && (
        <input
          type="text"
          value={customValue}
          onChange={(e) => onCustomChange(e.target.value)}
          placeholder="Specify reason..."
          maxLength={500}
          className="input-base text-sm"
          data-testid="reason-custom-input"
        />
      )}
    </div>
  );
}

function SuspendModal({ user, reasons, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [custom, setCustom] = useState("");
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const finalReason = reason === "Other" ? custom.trim() : reason;
    if (!finalReason) { toast.error("Pick a reason."); return; }
    if (hours <= 0) { toast.error("Duration must be > 0 hours."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/suspend`, { reason: finalReason, duration_hours: Number(hours) });
      toast.success(`Suspended ${user.email} for ${hours}h.`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to suspend.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={`SUSPEND · ${user.email}`} onClose={onClose}>
      <p className="text-xs text-muted-foreground mb-4">Cancels access immediately. User will see the reason and be able to appeal at login.</p>
      <ReasonPicker reasons={reasons} value={reason} onChange={setReason} customValue={custom} onCustomChange={setCustom} />
      <div className="mt-4">
        <label className="terminal-label block mb-2">Duration (hours)</label>
        <input
          type="number"
          min={1}
          max={8760}
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          className="input-base text-sm"
          data-testid="suspend-hours-input"
        />
        <div className="flex flex-wrap gap-1.5 mt-2">
          {[6, 24, 72, 168, 720].map((h) => (
            <button key={h} type="button" onClick={() => setHours(h)} className="text-[10px] tracking-[0.18em] uppercase font-mono px-2 py-1 border border-foreground/20 hover:border-primary hover:text-primary" data-testid={`suspend-quick-${h}h`}>
              {h < 24 ? `${h}h` : h < 168 ? `${h / 24}d` : h < 720 ? `${h / 168}w` : `${Math.round(h / 720)}mo`}
            </button>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onClose} className="btn-secondary text-xs">Cancel</button>
        <button type="button" onClick={submit} disabled={busy} className="btn-primary text-xs disabled:opacity-60" data-testid="suspend-confirm">
          {busy ? "Suspending..." : "Suspend"}
        </button>
      </div>
    </ModalShell>
  );
}

function BanModal({ user, reasons, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const finalReason = reason === "Other" ? custom.trim() : reason;
    if (!finalReason) { toast.error("Pick a reason."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/ban`, { reason: finalReason });
      toast.success(`Banned ${user.email}.`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to ban.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={`BAN · ${user.email}`} onClose={onClose} accent="destructive">
      <p className="text-xs text-destructive mb-4">Permanent ban. Cancels access immediately. User can appeal at login.</p>
      <ReasonPicker reasons={reasons} value={reason} onChange={setReason} customValue={custom} onCustomChange={setCustom} />
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onClose} className="btn-secondary text-xs">Cancel</button>
        <button type="button" onClick={submit} disabled={busy} className="btn-primary text-xs disabled:opacity-60" data-testid="ban-confirm">
          {busy ? "Banning..." : "Ban permanently"}
        </button>
      </div>
    </ModalShell>
  );
}

function ReinstateModal({ user, onClose, onDone }) {
  const [grantFree, setGrantFree] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/reinstate`, { grant_free_month: grantFree });
      toast.success(grantFree ? `Reinstated ${user.email} + granted 1 free month.` : `Reinstated ${user.email}.`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reinstate.");
    } finally {
      setBusy(false);
    }
  };

  const status = user.status || "active";
  return (
    <ModalShell title={`REINSTATE · ${user.email}`} onClose={onClose}>
      <p className="text-xs text-muted-foreground mb-4">
        Currently <span className="text-primary font-mono">{status.toUpperCase()}</span>. Reinstating clears the {status}, returns the user to active, and lets them log in immediately.
      </p>
      <label className="flex items-start gap-3 cursor-pointer p-3 border border-foreground/20 hover:border-primary/50">
        <input
          type="checkbox"
          checked={grantFree}
          onChange={(e) => setGrantFree(e.target.checked)}
          className="mt-0.5"
          data-testid="reinstate-grant-free-month"
        />
        <div>
          <div className="text-xs font-mono text-foreground/90">Grant 1 free month of access</div>
          <div className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground mt-1">Use this if you're walking back the {status} (maybe you were wrong, maybe they appealed well).</div>
        </div>
      </label>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onClose} className="btn-secondary text-xs">Cancel</button>
        <button type="button" onClick={submit} disabled={busy} className="btn-primary text-xs disabled:opacity-60" data-testid="reinstate-confirm">
          {busy ? "Reinstating..." : grantFree ? "Reinstate + grant month" : "Reinstate"}
        </button>
      </div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// Appeals panel
// ---------------------------------------------------------------------------
function AppealsPanel() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null); // appeal currently being resolved

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/appeals?status=${filter}`);
      setItems(r.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load appeals.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div data-testid="admin-appeals-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="terminal-label">[ APPEALS ]</div>
          <p className="text-xs text-muted-foreground mt-1">Submitted from the login page by banned/suspended users.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {["pending", "approved", "denied", "all"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              data-testid={`appeals-filter-${s}`}
              data-active={filter === s}
              className={`px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-mono border ${filter === s ? "border-primary text-primary" : "border-foreground/20 text-muted-foreground hover:text-foreground"}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
      ) : (items?.length || 0) === 0 ? (
        <div className="panel p-10 text-center text-muted-foreground" data-testid="appeals-empty">
          <Inbox className="w-8 h-8 mx-auto text-foreground/30 mb-3" strokeWidth={1.25} />
          <p className="text-sm">No appeals {filter !== "all" ? `(${filter})` : ""}.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="appeals-list">
          {items.map((a) => (
            <div key={a.id} className="panel p-5" data-testid={`appeal-row-${a.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-mono text-xs">{a.user_email}</span>
                    <Pill color={a.action_type === "banned" ? "primary" : "amber"} label={(a.action_type || "").toUpperCase()} />
                    <Pill color={a.status === "pending" ? "amber" : a.status === "approved" ? "green" : "muted"} label={(a.status || "").toUpperCase()} />
                  </div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground font-mono">
                    Submitted {fmtDate(a.created_at, true)}
                  </div>
                </div>
                {a.status === "pending" && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setResolving({ ...a, decision: "approve" })}
                      className="text-[10px] tracking-[0.2em] uppercase font-mono px-3 py-2 border border-accent-green/50 text-accent-green hover:bg-accent-green/10 inline-flex items-center gap-2"
                      data-testid={`appeal-approve-${a.id}`}
                    >
                      <Check className="w-3 h-3" /> Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => setResolving({ ...a, decision: "deny" })}
                      className="text-[10px] tracking-[0.2em] uppercase font-mono px-3 py-2 border border-destructive/50 text-destructive hover:bg-destructive/10 inline-flex items-center gap-2"
                      data-testid={`appeal-deny-${a.id}`}
                    >
                      <X className="w-3 h-3" /> Deny
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <div className="terminal-label mb-1">Original {a.action_type} reason</div>
                  <p className="text-xs text-foreground/75">{a.action_reason || "(none)"}</p>
                </div>
                <div>
                  <div className="terminal-label mb-1">Their appeal</div>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed text-foreground/90">{a.message}</p>
                </div>
                {a.status !== "pending" && (
                  <div className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground font-mono">
                    Resolved {fmtDate(a.resolved_at, true)} by {a.resolved_by || "(unknown)"}
                    {a.granted_free_month && " · granted 1 free month"}
                    {a.admin_decision && (
                      <div className="mt-1 normal-case tracking-normal text-foreground/70">Note: {a.admin_decision}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {resolving && (
        <AppealResolveModal
          appeal={resolving}
          onClose={() => setResolving(null)}
          onDone={() => { setResolving(null); load(); }}
        />
      )}
    </div>
  );
}

function AppealResolveModal({ appeal, onClose, onDone }) {
  const isApprove = appeal.decision === "approve";
  const [grantFree, setGrantFree] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/appeals/${appeal.id}/resolve`, {
        decision: appeal.decision,
        grant_free_month: isApprove ? grantFree : false,
        note: note.trim() || null,
      });
      toast.success(isApprove ? `Appeal approved · user reinstated${grantFree ? " + 1 free month" : ""}.` : "Appeal denied.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resolve.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={`${isApprove ? "APPROVE" : "DENY"} · ${appeal.user_email}`} onClose={onClose} accent={isApprove ? "primary" : "destructive"}>
      <p className="text-xs text-muted-foreground mb-4">
        {isApprove
          ? `Approving will reinstate the user and clear the ${appeal.action_type}. They'll be able to log in immediately.`
          : `Denying keeps the ${appeal.action_type} in place. The user will be told their appeal was reviewed and denied.`}
      </p>
      {isApprove && (
        <label className="flex items-start gap-3 cursor-pointer p-3 border border-foreground/20 hover:border-primary/50 mb-3">
          <input
            type="checkbox"
            checked={grantFree}
            onChange={(e) => setGrantFree(e.target.checked)}
            className="mt-0.5"
            data-testid="appeal-grant-free-month"
          />
          <div>
            <div className="text-xs font-mono text-foreground/90">Grant 1 free month of access</div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground mt-1">If you're walking back the decision and want to apologize with a free month.</div>
          </div>
        </label>
      )}
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        maxLength={1000}
        placeholder="Internal note (optional)..."
        className="input-base text-sm resize-y"
        data-testid="appeal-resolve-note"
      />
      <div className="flex justify-end gap-2 mt-4">
        <button type="button" onClick={onClose} className="btn-secondary text-xs">Cancel</button>
        <button type="button" onClick={submit} disabled={busy} className="btn-primary text-xs disabled:opacity-60" data-testid="appeal-resolve-confirm">
          {busy ? "..." : isApprove ? "Approve" : "Deny"}
        </button>
      </div>
    </ModalShell>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 text-[10px] tracking-[0.22em] uppercase text-muted-foreground font-mono">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}
function Dot({ color, label }) {
  const colorMap = {
    green: "bg-accent-green text-accent-green",
    amber: "bg-accent-amber text-accent-amber",
    red: "bg-destructive text-destructive",
    muted: "bg-foreground/30 text-muted-foreground",
  };
  const [bg, txt] = (colorMap[color] || colorMap.muted).split(" ");
  return (
    <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-mono">
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${bg}`} />
      <span className={txt}>{label}</span>
    </span>
  );
}

function pct(a, b) {
  if (!b) return 0;
  return Math.round((a / b) * 100);
}
function fmtDate(iso, withTime = false) {
  if (!iso) return " - ";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    if (!withTime) return date;
    const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    return `${date} · ${time}`;
  } catch { return iso; }
}
